import { TaskTypeV3_1 } from "@fartherfinance/frontend/api/Tasks/Types";
import { TransitionType } from "@fartherfinance/frontend/api/TransitionWorkflows/Types";

import { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";

import { createVirtualTaskId } from "./types";
import {
  advisorSuccessServiceTeam,
  businessOperationsServiceTeam,
  complianceServiceTeam,
  hrServiceTeam,
  itServiceTeam,
  onboardingServiceTeam,
  transitionsServiceTeam,
} from "./utils";

const currentEnv = process.env.WEBAPP_ENV || "UAT";
const envOnboardingServiceTeam = onboardingServiceTeam[currentEnv];
const envComplianceServiceTeam = complianceServiceTeam[currentEnv];
const envItServiceTeam = itServiceTeam[currentEnv];
const envBusinessOperationsServiceTeam =
  businessOperationsServiceTeam[currentEnv];
const envTransitionsServiceTeam = transitionsServiceTeam[currentEnv];
const envAdvisorSuccessServiceTeam = advisorSuccessServiceTeam[currentEnv];
const envHrServiceTeam = hrServiceTeam[currentEnv];

// Transition workflow configurations organized by transition type
const TRANSITION_WORKFLOW_CONFIG: Record<TransitionType, any> = {
  LPOA: {
    title: "LPOA Transition",
    description: "Limited Power of Attorney transition workflow",
    serviceTeam: envOnboardingServiceTeam,
    assignee: null,
    advisor: "primary-advisor",
    children: {
      "pre-launch": {
        id: createVirtualTaskId("pre-launch"),
        title: "Pre-Launch",
        taskType: {
          label: "Pre-Launch",
          value: "TRANSITION_KICKOFF",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("hold-kick-off-call"),
            title: "Hold Kick-off Call",
            description:
              "Recruiting will schedule this call to initiate the onboarding process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("weekly-onboarding-check-in"),
            title: "Weekly Onboarding Check-In",
            description: "Schedule weekly call for onboarding check-ins",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("create-advisor-folder"),
            title: "Create Advisor Folder",
            description:
              "Create the advisor folder to collect documents from advisor in the shared New Advisor Onboarding folder",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("kickoff-email"),
            title: "Kickoff Email",
            description:
              "Send 'Welcome to Farther' email to advisor with intake forms and onboarding resources",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("confirm-preferred-email"),
            title: "Confirm Preferred Farther Email",
            description:
              "After advisor confirms the naming convention they want for their email, we will notify HR so it can be set up correctly in Rippling",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-u4-2b-upload"),
            title: "Review U4/2B Upload and Check for Errors",
            description:
              "Check U4/2B has been uploaded to advisor folder, get confirmation from compliance it is ready for submission, dual register for merge",
            serviceTeam: envComplianceServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-book-analysis"),
            title: "Review Book Analysis",
            description:
              "Thoroughly check each section and notify various onboarding teams for setup prior to Day if needed",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-tech-procurement-form"),
            title: "Check Tech Procurement Form",
            description:
              "Make sure advisors are filling out technology procurement as soon as possible for a 2+ week delivery time frame",
            serviceTeam: envItServiceTeam,
            assignee: null,
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("confirm-tech-shipment"),
            title: "Confirm Tech Shipment",
            description:
              "Check on shipping details for the tech and escalate if there are delays",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-sma-alternatives-form"),
            title: "Review SMA/Alternatives Form",
            description:
              "Schedule introduction call to investments for introduction to FAM or if advisor has interest in separately managed accounts post launch",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-marketing-intake-form"),
            title: "Check Marketing Intake Form",
            description:
              "Check for branding requests, if so, notify marketing and IT team to address marketing materials and email/phone domains and migrations",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-insurance-annuities-form"),
            title: "Check Insurance/Annuities Form",
            description:
              "Schedule call with Insurance Team and DPL/Pinnacle to initiate transfer process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-compliance-team"),
            title: "Introduce to Compliance Team",
            description:
              "Review U4/2B and answer questions on registration process, client communication, etc.",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-investments-team"),
            title: "Introduce to Investments Team",
            description:
              "Review and answer questions on book analysis and SMA/Alts form",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-marketing-team"),
            title: "Introduce to Marketing Team",
            description:
              "Review and answer questions on branding and marketing intake form",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-pinnacle-dpl-team"),
            title: "Introduce to Pinnacle/DPL Team",
            description:
              "Review and answer questions on insurance and annuities transition process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-it-team"),
            title: "Introduce to IT Team",
            description:
              "Review and answer questions on devices, emails, phone numbers, subscriptions",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-financial-planning-team"),
            title: "Introduce to Financial Planning Team",
            description:
              "Answer questions related to financial planning and our financial planning software",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-hr-team"),
            title: "Introduce to HR Team",
            description:
              "Review and answer questions related to titles, benefits, Rippling, E&O policy, etc.",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("prepare-and-send-client-communication"),
            title: "Prepare and Send Client Communication",
            description:
              "Confirm language on either the negative consent letter or non-protocol client announcement and ensure a copy of NCL is received when sent to clients",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
        ],
      },
      launch: {
        id: createVirtualTaskId("launch"),
        title: "Launch",
        taskType: {
          label: "Launch",
          value: "TRANSITION_LAUNCH",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("send-formal-resignation-notice"),
            title: "Send in formal resignation notice",
            description:
              "Send your resignation letter and notify onboarding@farther.com with your holiday list",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("access-provisioning"),
            title: "Access & Provisioning",
            description:
              "IT will set up and help advisor log into Slack, Hubspot, Zoom, Gmail, and sync calendar",
            serviceTeam: envItServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("hold-day-1-call"),
            title: "Hold Day 1 Call",
            description:
              "Advisor is introduced to onboarding teams that will support them during the transition and is added to training calendar",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("send-client-announcement"),
            title: "Send Client Announcement",
            description:
              "Onboarding will prepare client announcement letter with advisor's new contact information, after getting approval from advisor, Onboarding will send via HubSpot",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("weekly-transition-check-in"),
            title: "Weekly Transition Check-In",
            description: "Schedule weekly call for transition check-ins",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("introduce-ria-manager"),
            title: "Introduce to RIA Manager",
            description:
              "RIA Manager will meet with advisor to provide guidance during their onboarding and continue to provide support after transition",
            serviceTeam: envAdvisorSuccessServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
        ],
      },
      "post-launch": {
        id: createVirtualTaskId("post-launch"),
        title: "Post-Launch",
        taskType: {
          label: "Post-Launch",
          value: "TRANSITION_POST_LAUNCH",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("crm-import-migration"),
            title: "CRM Import / Migration",
            description: "Upload contacts into HubSpot under advisor",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("performance-migration"),
            title: "Performance Migration",
            description:
              "After bulk of assets are at Farther, initiate conversation with business operations team to begin performance migration",
            serviceTeam: envBusinessOperationsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("hr-orientation"),
            title:
              "HR Orientation & Introduction to Farther Learning Management System (LMS)",
            description:
              "Ensure advisor attended HR orientation and have been introduced to LMS",
            serviceTeam: envHrServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("send-account-transfer-documents"),
            title: "Send Account Transfer Documents",
            description: null,
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("weekly-transition-check-ins"),
            title: "Weekly Transition Check-Ins",
            description:
              "Transitions will meet with advisor weekly to go over transition updates",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("introduce-cx-pod"),
            title: "Introduce to CX Pod",
            description:
              "Schedule introduction call to advisor's dedicated service team",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("check-on-insurance-annuities-progress"),
            title: "Check on Insurance / Annuities Progress",
            description:
              "Ensure conversations with Pinnacle/DPL are going well and see if escalations are needed",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("create-client-portals-with-pii"),
            title: "Create Client Portals with PII",
            description:
              "Transitions will bulk upload client data into Portal every week",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("blackdiamond-householding-and-billing"),
            title: "BlackDiamond Householding and Billing",
            description:
              "Accounts in BlackDiamond are assigned to households, advisor teams, and fee schedules created/assigned",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("farther-client-gift-boxes"),
            title: "Farther Client Gift Boxes",
            description:
              "Offer complimentary gift to advisors' clients after moving assets to Farther and add onto master gift box list",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("graduation-to-cx"),
            title: "Graduation to CX",
            description:
              "Hold final call with advisor and their CX team to formally conclude the transition",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
        ],
      },
      onboarding: {
        id: createVirtualTaskId("onboarding"),
        title: "Onboarding",
        taskType: {
          label: "Onboarding",
          value: "TRANSITION_ADVISOR_ONBOARDING",
        },
        serviceTeam: null,
        assignee: "inherited",
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("sign-in-to-gmail"),
            title: "Sign in to Gmail",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("create-email-signature"),
            title: "Create your email signature",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-rippling"),
            title: "Sign in to Rippling",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-greenhouse"),
            title: "Sign in to Greenhouse",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-google-calendar"),
            title: "Sign in to Google Calendar",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-hubspot"),
            title: "Sign in to Hubspot",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-zoom"),
            title: "Sign in to Zoom",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("meet-advisor-success-team"),
            title: "Meet the Advisor Success Team",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("meet-ria-team"),
            title: "Meet the RIA Team",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("log-in-to-lms"),
            title: "Log in to Learning Management System",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("access-advisor-training"),
            title:
              'Access ongoing Advisor training by hitting "add calendar" in the email you\'ll receive',
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("schedule-portfolio-review"),
            title: "Schedule Portfolio review with office of the CIO",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("take-compliance-training"),
            title: "Take your compliance training",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
        ],
      },
    },
  },
  ACAT: {
    title: "ACATS Transition",
    description: "ACATS account transfer workflow",
    serviceTeam: envOnboardingServiceTeam,
    assignee: null,
    advisor: "primary-advisor",
    children: {
      "pre-launch": {
        id: createVirtualTaskId("pre-launch"),
        title: "Pre-Launch",
        taskType: {
          label: "Pre-Launch",
          value: "TRANSITION_KICKOFF",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("hold-kickoff-call"),
            title: "Hold Kickoff Call",
            description:
              "Recruiting will schedule this call to initiate the onboarding process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("weekly-onboarding-check-in"),
            title: "Weekly Onboarding Check-In",
            description: "Schedule weekly call for onboarding check-ins",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("create-advisor-folder"),
            title: "Create Advisor Folder",
            description:
              "Create the advisor folder to collect documents from advisor in the shared New Advisor Onboarding folder",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("kickoff-email"),
            title: "Kickoff Email",
            description:
              "Send 'Welcome to Farther' email to advisor with intake forms and onboarding resources",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("confirm-preferred-farther-email"),
            title: "Confirm Preferred Farther Email",
            description:
              "After advisor confirms the naming convention they want for their email, we will notify HR so it can be set up correctly in Rippling",
            serviceTeam: envComplianceServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-u42b-upload-and-check-for-errors"),
            title: "Review U4/2B Upload and Check for Errors",
            description:
              "Check U4/2B has been uploaded to advisor folder, get confirmation from compliance it is ready for submission, dual register for merge",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-book-analysis"),
            title: "Review Book Analysis",
            description:
              "Thoroughly check each section and notify various onboarding teams for setup prior to Day if needed",
            serviceTeam: envItServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-tech-procurement-from"),
            title: "Check Tech Procurement From",
            description:
              "Make sure advisors are filling out technology procurement as soon as possible for a 2+ week delivery time frames",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("confirm-tech-shipment"),
            title: "Confirm Tech Shipment",
            description:
              "Check on shipping details for the tech and escalate if there are delays",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-sma-alternatives-form"),
            title: "Review SMA/Alternatives Form",
            description:
              "Schedule introduction call to investments for introduction to FAM or if advisor has interest in separately managed accounts post launch",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-marketing-intake-form"),
            title: "Check Marketing Intake Form",
            description:
              "Check for branding requests, if so, notify marketing and IT team to address marketing materials and email/phone domains and migrations",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-insurance-annuities-form"),
            title: "Check Insurance/Annuities Form",
            description:
              "Schedule call with Insurance Team and DPL/Pinnacle to initiate transfer process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-to-compliance-team"),
            title: "Introduce to Compliance Team",
            description:
              "Review U4/2B and answer questions on registration process, client communication, etc.",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-to-investments-team"),
            title: "Introduce to Investments Team",
            description:
              "Review and answer questions on book analysis and SMA/Alts form",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-to-marketing-team"),
            title: "Introduce to Marketing Team",
            description:
              "Review and answer questions on branding and marketing intake form",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-to-pinnacle-dpl-team"),
            title: "Introduce to Pinnacle/DPL Team",
            description:
              "Review and answer questions on insurance and annuities transition process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-to-it-team"),
            title: "Introduce to IT Team",
            description:
              "Review and answer questions on devices, emails, phone numbers, subscriptions",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-to-financial-planning-team"),
            title: "Introduce to Financial Planning Team",
            description:
              "Answer questions related to financial planning and our financial planning software",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-to-hr-team"),
            title: "Introduce to HR Team",
            description:
              "Review and answer questions related to titles, benefits, Rippling, E&O policy, etc.",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("prepare-and-send-client-communication"),
            title: "Prepare and Send Client Communication",
            description:
              "Confirm language on either the negative consent letter or non-protocol client announcement and ensure a copy of NCL is received when sent to clients",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
        ],
      },
      launch: {
        id: createVirtualTaskId("launch"),
        title: "Launch",
        taskType: {
          label: "Launch",
          value: "TRANSITION_LAUNCH",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("send-in-formal-resignation-notice"),
            title: "Send in formal resignation notice",
            description:
              "Send your resignation letter and notify onboarding@farther.com with your holiday list",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("access-and-provisioning"),
            title: "Access & Provisioning",
            description:
              "IT will set up and help advisor log into Slack, Hubspot, Zoom, Gmail, and sync calendar",
            serviceTeam: envItServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("hold-day-one-call"),
            title: "Hold Day 1 Call",
            description:
              "Advisor is introduced to onboarding teams that will support them during the transition and is added to training calendar",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("send-client-announcement"),
            title: "Send Client Announcement",
            description:
              "Onboarding will prepare client announcement letter with advisor's new contact information, after getting approval from advisor, Onboarding will send via HubSpot",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("weekly-transition-check-in"),
            title: "Weekly Transition Check-In",
            description: "Schedule weekly call for transition check-ins",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("introduce-to-ria-manager"),
            title: "Introduce to RIA Manager",
            description:
              "RIA Manager will meet with advisor to provide guidance during their onboarding and continue to provide support after transition",
            serviceTeam: envAdvisorSuccessServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
        ],
      },
      "post-launch": {
        id: createVirtualTaskId("post-launch"),
        title: "Post-Launch",
        taskType: {
          label: "Post-Launch",
          value: "TRANSITION_POST_LAUNCH",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("crm-import-migration"),
            title: "CRM Import / Migration",
            description: "Upload contacts into HubSpot under advisor",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("performance-migration"),
            title: "Performance Migration",
            description:
              "After bulk of assets are at Farther, initiate conversation with business operations team to begin performance migration",
            serviceTeam: envBusinessOperationsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId(
              "hr-orientation-and-introduction-to-farther-lms"
            ),
            title:
              "HR Orientation & Introduction to Farther Learning Management System (LMS)",
            description:
              "Ensure advisor attended HR orientation and have been introduced to LMS",
            serviceTeam: envHrServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("send-account-transfer-documents"),
            title: "Send Account Transfer Documents",
            description:
              "Transitions will meet with advisor weekly to go over transition updates",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("introduction-to-cx-pod"),
            title: "Introduction to CX Pod",
            description:
              "Schedule introduction call to advisor's dedicated service team",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("check-on-insurance-annuities-progress"),
            title: "Check on Insurance / Annuities Progress",
            description:
              "Ensure conversations with Pinnacle/DPL are going well and see if escalations are needed",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("create-client-portals-with-pii"),
            title: "Create Client Portals with PII",
            description:
              "Transitions will bulk upload client data into Portal every week",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("blackdiamond-householding-and-billing"),
            title: "BlackDiamond Householding and Billing",
            description:
              "Accounts in BlackDiamond are assigned to households, advisor teams, and fee schedules created/assigned",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("farther-client-gift-boxes"),
            title: "Farther Client Gift Boxes",
            description:
              "Offer complimentary gift to advisors' clients after moving assets to Farther and add onto master gift box list",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("graduation-to-cs"),
            title: "Graduation to CX",
            description:
              "Hold final call with advisor and their CX team to formally conclude the transition",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
        ],
      },
      onboarding: {
        id: createVirtualTaskId("onboarding"),
        title: "Onboarding",
        taskType: {
          label: "Onboarding",
          value: "TRANSITION_ADVISOR_ONBOARDING",
        },
        serviceTeam: null,
        assignee: "inherited",
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("sign-in-to-gmail"),
            title: "Sign in to Gmail",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("create-email-signature"),
            title: "Create your email signature",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-rippling"),
            title: "Sign in to Rippling",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-greenhouse"),
            title: "Sign in to Greenhouse",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-google-calendar"),
            title: "Sign in to Google Calendar",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-hubspot"),
            title: "Sign in to Hubspot",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-zoom"),
            title: "Sign in to Zoom",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("meet-advisor-success-team"),
            title: "Meet the Advisor Success Team",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("meet-ria-team"),
            title: "Meet the RIA Team",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("log-in-to-lms"),
            title: "Log in to Learning Management System",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("access-advisor-training"),
            title:
              'Access ongoing Advisor training by hitting "add calendar" in the email you\'ll receive',
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("schedule-portfolio-review"),
            title: "Schedule Portfolio review with office of the CIO",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("take-compliance-training"),
            title: "Take your compliance training",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
        ],
      },
    },
  },
  MasterMerge: {
    title: "Merge Transition",
    description: "Account merge transition workflow",
    serviceTeam: envOnboardingServiceTeam,
    assignee: null,
    advisor: "primary-advisor",
    children: {
      "pre-launch": {
        id: createVirtualTaskId("pre-launch"),
        title: "Pre-Launch",
        taskType: {
          label: "Pre-Launch",
          value: "TRANSITION_KICKOFF",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("hold-kick-off-call"),
            title: "Hold Kick-off Call",
            description:
              "Recruiting will schedule this call to initiate the onboarding process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("weekly-onboarding-check-in"),
            title: "Weekly Onboarding Check-In",
            description: "Schedule weekly call for onboarding check-ins",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("create-advisor-folder"),
            title: "Create Advisor Folder",
            description:
              "Create the advisor folder to collect documents from advisor in the shared New Advisor Onboarding folder",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("kickoff-email"),
            title: "Kickoff Email",
            description:
              "Send 'Welcome to Farther' email to advisor with intake forms and onboarding resources",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("confirm-preferred-email"),
            title: "Confirm Preferred Farther Email",
            description:
              "After advisor confirms the naming convention they want for their email, we will notify HR so it can be set up correctly in Rippling",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-u4-2b-upload"),
            title: "Review U4/2B Upload and Check for Errors",
            description:
              "Check U4/2B has been uploaded to advisor folder, get confirmation from compliance it is ready for submission, dual register for merge",
            serviceTeam: envComplianceServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-book-analysis"),
            title: "Review Book Analysis",
            description:
              "Thoroughly check each section and notify various onboarding teams for setup prior to Day if needed",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-tech-procurement-form"),
            title: "Check Tech Procurement Form",
            description:
              "Make sure advisors are filling out technology procurement as soon as possible for a 2+ week delivery time frame",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("confirm-tech-shipment"),
            title: "Confirm Tech Shipment",
            description:
              "Check on shipping details for the tech and escalate if there are delays",
            serviceTeam: envItServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("review-sma-alternatives-form"),
            title: "Review SMA/Alternatives Form",
            description:
              "Schedule introduction call to investments for introduction to FAM or if advisor has interest in separately managed accounts post launch",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-marketing-intake-form"),
            title: "Check Marketing Intake Form",
            description:
              "Check for branding requests, if so, notify marketing and IT team to address marketing materials and email/phone domains and migrations",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("check-insurance-annuities-form"),
            title: "Check Insurance/Annuities Form",
            description:
              "Schedule call with Insurance Team and DPL/Pinnacle to initiate transfer process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("submit-notice-to-custodian"),
            title: "Submit Notice to Custodian",
            description:
              "After advisor confirmation, inform business operations team to send notice to the custodian",
            serviceTeam: envBusinessOperationsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-compliance-team"),
            title: "Introduce to Compliance Team",
            description:
              "Review U4/2B and answer questions on registration process, client communication, etc.",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-investments-team"),
            title: "Introduce to Investments Team",
            description:
              "Review and answer questions on book analysis and SMA/Alts form",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-marketing-team"),
            title: "Introduce to Marketing Team",
            description:
              "Review and answer questions on branding and marketing intake form",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-pinnacle-dpl-team"),
            title: "Introduce to Pinnacle/DPL Team",
            description:
              "Review and answer questions on insurance and annuities transition process",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-it-team"),
            title: "Introduce to IT Team",
            description:
              "Review and answer questions on devices, emails, phone numbers, subscriptions",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-financial-planning-team"),
            title: "Introduce to Financial Planning Team",
            description:
              "Answer questions related to financial planning and our financial planning software",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("introduce-hr-team"),
            title: "Introduce to HR Team",
            description:
              "Review and answer questions related to titles, benefits, Rippling, E&O policy, etc.",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
          {
            id: createVirtualTaskId("prepare-and-send-client-communication"),
            title: "Prepare and Send Client Communication",
            description:
              "Confirm language on either the negative consent letter or non-protocol client announcement and ensure a copy of NCL is received when sent to clients",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Pre-Launch", value: "TRANSITION_KICKOFF" },
          },
        ],
      },
      launch: {
        id: createVirtualTaskId("launch"),
        title: "Launch",
        taskType: {
          label: "Launch",
          value: "TRANSITION_LAUNCH",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("custodian-3-day-migration-sequence"),
            title: "Custodian 3-day Migration Sequence",
            description:
              "Custodian confirms migration start date in advance of start date. Day 1- prep day for custodian. BAU for Farther + joining team. Day 2- Fido moves w/o calls. Schwab calls to confirm start and calls to confirm complete. Day 3- Rep codes assigned in custodian portal & Black Diamond.",
            serviceTeam: envBusinessOperationsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("hold-day-1-call"),
            title: "Hold Day 1 Call",
            description:
              "Advisor is introduced to onboarding teams that will support them during the transition and is added to training calendar",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("weekly-transition-check-in"),
            title: "Weekly Transition Check-In",
            description: "Schedule weekly call for transition check-ins",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
          {
            id: createVirtualTaskId("introduce-ria-manager"),
            title: "Introduce to RIA Manager",
            description:
              "RIA Manager will meet with advisor to provide guidance during their onboarding and continue to provide support after transition",
            serviceTeam: envAdvisorSuccessServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Launch", value: "TRANSITION_LAUNCH" },
          },
        ],
      },
      "post-launch": {
        id: createVirtualTaskId("post-launch"),
        title: "Post-Launch",
        taskType: {
          label: "Post-Launch",
          value: "TRANSITION_POST_LAUNCH",
        },
        serviceTeam: envOnboardingServiceTeam,
        assignee: null,
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("hr-orientation"),
            title:
              "HR Orientation & Introduction to Farther Learning Management System (LMS)",
            description:
              "Ensure advisor attended HR orientation and have been introduced to LMS",
            serviceTeam: envHrServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("introduce-cx-pod"),
            title: "Introduce to CX Pod",
            description:
              "Schedule introduction call to advisor's dedicated service team",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("crm-import-migration"),
            title: "CRM Import / Migration",
            description: "Upload contacts into HubSpot under advisor",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("create-shell-portals"),
            title: "Create Shell Portals",
            description:
              "After confirming accounts are in BlackDiamond, Transitions team will create shell portals for advisors within 2 weeks of launch date",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("check-on-insurance-annuities-progress"),
            title: "Check on Insurance / Annuities Progress",
            description:
              "Ensure conversations with Pinnacle/DPL are going well and see if escalations are needed",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("blackdiamond-householding-and-billing"),
            title: "BlackDiamond Householding and Billing",
            description:
              "Accounts in BlackDiamond are assigned to households, advisor teams, and fee schedules created/assigned",
            serviceTeam: envOnboardingServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("performance-migration"),
            title: "Performance Migration",
            description:
              "After bulk of assets are at Farther, initiate conversation with business operations team to begin performance migration",
            serviceTeam: envBusinessOperationsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("farther-client-gift-boxes"),
            title: "Farther Client Gift Boxes",
            description:
              "Offer complimentary gift to advisors' clients after moving assets to Farther and add onto master gift box list",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
          {
            id: createVirtualTaskId("graduation-to-cx"),
            title: "Graduation to CX",
            description:
              "Hold final call with advisor and their CX team to formally conclude the transition",
            serviceTeam: envTransitionsServiceTeam,
            assignee: null,
            advisor: "primary-advisor",
            taskType: { label: "Post-Launch", value: "TRANSITION_POST_LAUNCH" },
          },
        ],
      },
      onboarding: {
        id: createVirtualTaskId("onboarding"),
        title: "Onboarding",
        taskType: {
          label: "Onboarding",
          value: "TRANSITION_ADVISOR_ONBOARDING",
        },
        serviceTeam: null,
        assignee: "inherited",
        advisor: "primary-advisor",
        children: [
          {
            id: createVirtualTaskId("sign-in-to-gmail"),
            title: "Sign in to Gmail",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("create-email-signature"),
            title: "Create your email signature",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-rippling"),
            title: "Sign in to Rippling",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-greenhouse"),
            title: "Sign in to Greenhouse",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-google-calendar"),
            title: "Sign in to Google Calendar",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-hubspot"),
            title: "Sign in to Hubspot",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("sign-in-to-zoom"),
            title: "Sign in to Zoom",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("meet-advisor-success-team"),
            title: "Meet the Advisor Success Team",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("meet-ria-team"),
            title: "Meet the RIA Team",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("log-in-to-lms"),
            title: "Log in to Learning Management System",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("access-advisor-training"),
            title:
              'Access ongoing Advisor training by hitting "add calendar" in the email you\'ll receive',
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("schedule-portfolio-review"),
            title: "Schedule Portfolio review with office of the CIO",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
          {
            id: createVirtualTaskId("take-compliance-training"),
            title: "Take your compliance training",
            description: null,
            serviceTeam: null,
            assignee: "inherited",
            advisor: "primary-advisor",
            taskType: {
              label: "Onboarding",
              value: "TRANSITION_ADVISOR_ONBOARDING",
            },
          },
        ],
      },
    },
  },
} as const;

// Helper function to get workflow config for a specific transition type
const getWorkflowConfigForTransitionType = (transitionType: TransitionType) => {
  return (
    TRANSITION_WORKFLOW_CONFIG[transitionType] ||
    TRANSITION_WORKFLOW_CONFIG.LPOA
  );
};

// Get workflow phases for a specific transition type
const getWorkflowPhasesForTransitionType = (transitionType: TransitionType) => {
  const config = getWorkflowConfigForTransitionType(transitionType);
  return config.children;
};

// Get root task IDs for a specific transition type
export const getRootTaskIdsForTransitionType = (
  transitionType: TransitionType
) => {
  const phases = getWorkflowPhasesForTransitionType(transitionType);
  return Object.keys(phases);
};

// Get root task definitions for a specific transition type
export const getRootTaskDefinitionsForTransitionType = (
  transitionType: TransitionType
) => {
  const phases = getWorkflowPhasesForTransitionType(transitionType);
  return Object.values(phases).map((phaseConfig: any) => ({
    id: phaseConfig.id,
    title: phaseConfig.title,
  }));
};

// Get task type mapping for a specific transition type
export const getRootTaskTypeMappingForTransitionType = (
  transitionType: TransitionType
) => {
  const phases = getWorkflowPhasesForTransitionType(transitionType);
  return Object.fromEntries(
    Object.values(phases).map((phaseConfig: any) => [
      phaseConfig.id,
      phaseConfig.taskType,
    ])
  ) as Record<string, { label: string; value: string }>;
};

// Get children catalog for a specific transition type
export const getChildrenCatalogForTransitionType = (
  transitionType: TransitionType
) => {
  const phases = getWorkflowPhasesForTransitionType(transitionType);
  return Object.fromEntries(
    Object.values(phases).map((phaseConfig: any) => [
      phaseConfig.title,
      phaseConfig.children || [], // Returns array of objects instead of strings
    ])
  );
};

export const getServiceTeamForTransitionType = (
  transitionType: TransitionType
) => {
  const config = getWorkflowConfigForTransitionType(transitionType);
  return config.serviceTeam || null;
};

// Get assignee configuration for a specific transition type
export const getAssigneeForTransitionType = (
  transitionType: TransitionType
) => {
  const config = getWorkflowConfigForTransitionType(transitionType);
  return config.assignee || null;
};

export const getTaskTypeForPhase = (
  transitionType: TransitionType,
  phaseTitle: string
): EnhancedAutocompleteOption<TaskTypeV3_1> | undefined => {
  const config = getWorkflowConfigForTransitionType(transitionType);

  if (!config || !config.children) {
    return undefined;
  }

  const phases = Object.values(config.children) as Array<{
    title: string;
    taskType: EnhancedAutocompleteOption<TaskTypeV3_1>;
  }>;

  const phase = phases.find((p) => p.title === phaseTitle);
  return phase?.taskType;
};

// Aggregate all root task type mappings for all transition types
export const ROOT_TASK_TYPE_MAPPING = (
  Object.keys(TRANSITION_WORKFLOW_CONFIG) as TransitionType[]
).reduce((acc, transitionType) => {
  const mapping = getRootTaskTypeMappingForTransitionType(transitionType);
  return { ...acc, ...mapping };
}, {} as Record<string, { label: string; value: string }>);
